"use client";
import SectionPage from "@/components/common/section-page";

import {Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList,} from "@/components/ui/breadcrumb";
import CardTitle from "@/components/common/card-title";
import AwesomeCard from "@/components/common/awesome-card";
import { useNavigate } from "react-router-dom";
import {cn} from "@/lib/utils";

interface ItemProps {
    title: string;
    text: string;
    image: string;
    sens: "ltr" | "rtl";
}

function Item({title, text, image, sens}: ItemProps) {
    return (
        <div className={cn("grid grid-cols-1 md:grid-cols-2 gap-4")}>
            <div className={cn(sens === "rtl" ? "order-2" : "order-1")}>
                <h3 className={"pb-8 text-bleu-marine font-exo text-xl font-bold"}>{title}</h3>
                <p>{text}</p>
            </div>
            <div className={cn(sens === "rtl" ? "order-1" : "order-2")}>
                <img src={image} alt={"image"} className={"w-full"}/>
            </div>
        </div>
    )
}

export default function Home() {
    const navigate = useNavigate();

    return (
        <div className={"flex flex-col gap-6 pb-6 bg-slate-100"}>
            <CardTitle
                title={"Art de vivre et Gastronomie d'ici"}
                backgroundImgSrc={"/images/card-title/attieke-1.png"}
            />

            {/* Breadcrumb */}
            <SectionPage sectionType="2xl">
                <div className={"flex justify-between items-center"}>
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem>
                                <BreadcrumbLink href={"/"}>Art de vivre et Gastronomie d'ici</BreadcrumbLink>
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>
                </div>
            </SectionPage>

            {/* Nature & Biodiversité */}
            <SectionPage sectionType="2xl">
                <AwesomeCard
                    title={"Art de vivre et Gastronomie d'ici"}
                    className={"bg-white text-muted-foreground"}
                    titleClassName={"text-bleu-marine"}
                >
                    La gastronomie de la Côte d’Ivoire est une manifestation de sa culture qui comporte différents plats
                    provenant de tous les groupes ethniques qui constituent la population du pays. Les spécialités
                    culinaires ont subi également l’influence de celles des pays de la sous-région fortement représentée
                    en terre ivoirienne.
                    <br/>
                    <br/>
                    Si les Ivoiriens aiment bien manger et sont fiers de leur gastronomie et de leurs spécialités
                    culinaires, ils ont su faire une place de choix aux cuisines venues d’ailleurs. Pays multiculturel
                    et ouvert sur le monde, le pays propose une gastronomie variée et métissée.
                    <br/>
                    <br/>
                    La plupart des grandes villes comptent de nombreux restaurants français, libanais, chinois,
                    italiens, maghrébins, africains, etc. Si vous avez le mal du pays, vous ne devriez pas avoir trop de
                    difficultés à trouver des spécialités de votre pays.
                    <br/>
                    <br/>
                    Pour les foodies, la cuisine Ivoirienne épicée et pimentée met en musique les produits locaux dans
                    une atmosphère festive de sorte à partager avec les autres une expérience culturelle gustative
                    valorisante et d’exprimer de fort belle manière l’hospitalité à l’ivoirienne.
                    <br/>
                    <br/>
                    En Côte d’Ivoire, manger c’est fêter car la cuisine ivoirienne répond bien plus qu’au simple besoin
                    de se sustenter. Manger ivoirien, c’est vivre un moment de pur délice dans une ambiance festive,
                    chaleureuse et conviviale. Les repas sont colorés, variés, gouteux, épicés, des fois sucrés ou
                    piquants et finalement, ils sont pleinement nutritifs. Pour un initié comme pour un adepte de longue
                    date, la cuisine ivoirienne a toujours de quoi éveiller tous ses sens.
                    <br/>
                    <br/>
                    Pour les touristes dont la dégustation de produits alimentaires et de boissons est l’une des
                    principales raisons de visiter une nouvelle destination, elle est la destination pour faire voyager
                    vos papilles vers divers styles gastronomiques soutenus par diverses sonorités musicales.
                    <br/>
                    <br/>
                    Ainsi de nombreux plats régionaux se sont développés au point d’être connus et reconnus au niveau
                    international, le poulet et le poisson braisé à l’Ivoirienne accompagné de l’Atiéké, le kédjénou de
                    poulet cuit à l’étouffé avec des légumes, le foutou de banane ou d’igname à la sauce graine, le
                    placali de manioc à la sauce gombo, donnant parfois naissance à des variations d’une région à
                    l’autre.
                    <br/>
                    <br/>
                    Ces mets fort appréciés se retrouvent aussi bien sur les cartes de grande table mais également au
                    menu des maquis type de restaurant servant de lieu de retrouvailles, de socialisation, souvent à
                    ciel ouvert généralement ouvert de jours comme de nuits.
                    <br/>
                    <br/>
                    Le street food n’est pas en reste avec l’alloco, le garba, les beignets aller-retour, le choukouya
                    de mouton et de bœuf braisé, etc et leurs maquis spécialisés, allocodrome, garbadrome, etc
                    <br/>
                    <br/>
                    La cuisine ivoirienne est riche et la culture gastronomique forte est constamment célébrée par les
                    nombreux festivals. Amoureux de viandes, volailles, poissons et crustacés, amateur de sauce et
                    potage, abonné aux kebabs et pizzas, etc, vous trouverez sûrement de quoi combler votre appétit et
                    votre gourmandise.
                </AwesomeCard>
            </SectionPage>

            {/* Quiétude et Félicité */}
            <SectionPage sectionType="2xl">
                <div className={"flex flex-col gap-4"}>
                    <Item title={"Garba"} text={`Street Food
Le garba (différent de l’attiéké – poisson) est un plat populaire ivoirien très bourratif fait à base de semoule de manioc, communément appelée attiéké, accompagnée de thon salé frit. Il est généralement garni de piments frais , de tomates, l’oignons, de bouillons cube et de mayonnaise.
Il est servi dans des baraques communément appelées « garbadrommes » par des hommes haoussa (d’origine nigérienne). « Garba » qui signifie « garçon », est un d’ailleurs un nom très populaire au Niger.
Il est possible de s’offrir un plat de garba à partir de 300 F CFA.`}
                          image={"/images/art-de-vivre-gastro/garba.png"}
                          sens={"ltr"}/>

                    <Item title={"Akpessi"} text={`AKPESSI DE BANANE
L’akpessi est un plat traditionnel de Côte d’Ivoire.Il s’accompagne d’une sauce simple et légère préparée avec des aubergines africaines, des tomates, des oignons et du poisson.`}
                          image={"/images/art-de-vivre-gastro/apessi.png"}
                          sens={"rtl"}/>

                    <Item title={"Foutou banane, sauce Assiaploua"} text={`Le foutou banane accompagné de la sauce Assiaploua.
Un plat des Agnis de l’indenie djuablin. Il est fait à base de la banane plantain mélangée avec du manioc pour le foutou. Quant à la sauce, elle est concoctée avec les feuilles appelées Assiaploua et d’autres ingrédients dont la viande fumée, les escargots, les crabes, le poisson brochet, le poisson sec et bien d’autres…`}
                          image={"/images/art-de-vivre-gastro/assiaploua.png"}
                          sens={"ltr"}/>

                    <Item title={"Foutou banane, sauce aubergine"} text={`La sauce aubergine avec du foutou banane , un plat Akan spécialement prisé en pays Agni.`}
                          image={"/images/art-de-vivre-gastro/sauce-aubergine.png"}
                          sens={"rtl"}/>

                    <Item title={"Le Mankou N’zoué aux escargots"} text={`Le Mankou N’zoué aux escargots – Spécialité Agni.`}
                          image={"/images/art-de-vivre-gastro/escargot.png"}
                          sens={"ltr"}/>

                    <Item title={"Le croupion de dinde"} text={`Le croupion de dinde`}
                          image={"/images/art-de-vivre-gastro/croupion2.png"}
                          sens={"rtl"}/>

                    <Item title={"N’DEFOU"} text={`LE N’DEFOU ou « banane non mûre », est un foufou à base de banane verte,accompagné d’une sauce avec du gibier ou des tripes🍲. C’est une spécialité du peuple abbey, situé dans la région de l’Agneby Tiassa.`}
                          image={"/images/art-de-vivre-gastro/foufou.png"}
                          sens={"ltr"}/>

                    <Item title={"Attieke & Omelette"} text={`Attieke & Omelette
Ce plat a sauvé de nombreuses vies en Côte d’Ivoire`}
                          image={"/images/art-de-vivre-gastro/attiekeomelette.png"}
                          sens={"rtl"}/>
                </div>
            </SectionPage>
        </div>
    );
}
